import type { OrganizationRole } from '@onefootprint/request-types/dashboard';
import { useToast } from '@onefootprint/ui';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationDialog from './components/confirmation-dialog';
import useRemoveRole from './hooks/use-remove-role';

export type RemoveHandler = {
  remove: () => void;
};

export type RemoveProps = {
  role: OrganizationRole;
};

const Remove = forwardRef<RemoveHandler, RemoveProps>(({ role }, ref) => {
  const { t } = useTranslation('roles', { keyPrefix: 'remove' });
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { id, numActiveUsers, name, numActiveApiKeys } = role;
  const removeRoleMutation = useRemoveRole(name);

  const showConfirmation = () => {
    setOpen(true);
  };

  const hideConfirmation = () => {
    setOpen(false);
  };

  const remove = () => {
    removeRoleMutation.mutate(id, {
      onSuccess: hideConfirmation,
    });
  };

  const handleRemove = () => {
    if (numActiveUsers && numActiveUsers > 0) {
      toast({
        title: t($ => $.errors.numActiveUsers.title),
        description: t($ =>
          numActiveUsers === 1 ? $.errors.numActiveUsers.description_one : $.errors.numActiveUsers.description_other,
        ),
        variant: 'error',
      });
    } else if (numActiveApiKeys && numActiveApiKeys > 0) {
      toast({
        title: t($ => $.errors.numActiveApiKeys.title),
        description: t($ =>
          numActiveApiKeys === 1
            ? $.errors.numActiveApiKeys.description_one
            : $.errors.numActiveApiKeys.description_other,
        ),
        variant: 'error',
      });
    } else {
      showConfirmation();
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      remove: handleRemove,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <ConfirmationDialog
      isPending={removeRoleMutation.isPending}
      onClose={hideConfirmation}
      onConfirm={remove}
      open={open}
      title={t($ => $.confirmation.title)}
    >
      <Trans
        ns="roles"
        i18nKey={$ => $.remove.confirmation.description}
        components={{
          b: <b className="text-label-2" />,
        }}
        values={{ name }}
      />
    </ConfirmationDialog>
  );
});

export default Remove;
